import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import FETCH from "../../../utils/fetch";
import "../LiabilityDashboard.css";
import debtImage from "../../../assets/images/debt.png";
import { ChartColors } from "../../../components/ChartColors";
import getFAClientName from "../../../helpers/getFAClientName";
import { exportElementToPaginatedPdf } from "../../../utils/pdfExport";
import { LiabilityPDFReport } from "./LiabilityPDFReport";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import MODULE_API from "../../../apis/module.api";
import { captureChartImage, sleep, waitForNextPaint } from "../../../utils/chartExport";

export const LiabilityReport = React.forwardRef((props, ref) => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [loanChartImage, setLoanChartImage] = useState(null);
  const [creditChartImage, setCreditChartImage] = useState(null);
  const loanChartRef = useRef(null);
  const creditChartRef = useRef(null);

  // Dedicated PDF lists to prevent live UI pollution
  const [pdfLoanList, setPdfLoanList] = useState([]);
  const [pdfCreditList, setPdfCreditList] = useState([]);
  const [pdfFutureList, setPdfFutureList] = useState([]);
  const latestLoadingRef = useRef(props.loading);
  const latestChartLoadingRef = useRef(props.loadingLiabilityChartData);
  const latestLoanDataRef = useRef(props.allLiabilitiesForChart || []);
  const latestCreditDataRef = useRef(props.allCreditRowsForChart || []);

  useEffect(() => {
    latestLoadingRef.current = props.loading;
    latestChartLoadingRef.current = props.loadingLiabilityChartData;
    latestLoanDataRef.current = props.allLiabilitiesForChart || [];
    latestCreditDataRef.current = props.allCreditRowsForChart || [];
  }, [
    props.loading,
    props.loadingLiabilityChartData,
    props.allLiabilitiesForChart,
    props.allCreditRowsForChart,
  ]);

  const loaderIcon = <LoadingOutlined style={{ fontSize: 24, color: "#2a3da3" }} spin />;
  const valueLoaderIcon = <LoadingOutlined style={{ fontSize: 14, color: "#ffffff" }} spin />;

  React.useImperativeHandle(ref, () => ({
    downloadPDF: () => {
      downloadPDF();
    },
    isDownloading: downloadLoading,
  }));

  const formatCurrency = (value) =>
    `$${parseFloat(value || 0)
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

  const renderReportValue = (value, showAsTotal = false) => {
    if (!props.loading) return formatCurrency(value);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          color: "#ffffff",
          fontSize: showAsTotal ? "16px" : "14px",
          fontWeight: 600,
        }}
      >
        <Spin indicator={valueLoaderIcon} />
        Loading...
      </span>
    );
  };

  const fetchAllModuleDataForPDF = async (moduleName) => {
    let allData = [];
    let page = 1;
    let hasMore = true;
    const limit = 1000;
    const userRole =
      localStorage.getItem("role") != "undefined" &&
      JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");

    while (hasMore) {
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id:
          userRole === "Financial Advisor Client"
            ? `?module=${moduleName}&page=${page}&limit=${limit}&clientId=${ID}`
            : `?module=${moduleName}&page=${page}&limit=${limit}`,
        body: {},
      });

      if (datares && datares.records && datares.records.length > 0) {
        allData = [...allData, ...datares.records];
        if (datares.records.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    return allData;
  };

  const downloadPDF = async () => {
    try {
      setDownloadLoading(true);
      if (props.onDownloadLoading) props.onDownloadLoading(true);

      for (let i = 0; i < 30; i += 1) {
        const chartsStillLoading =
          latestLoadingRef.current || latestChartLoadingRef.current;
        const hasAnyChartData =
          latestLoanDataRef.current.length > 0 || latestCreditDataRef.current.length > 0;

        if (!chartsStillLoading && hasAnyChartData) {
          break;
        }

        await waitForNextPaint();
        await sleep(200);
      }

      await waitForNextPaint();

      // 1. Capture charts only after the hidden chart canvases have painted.
      const capturedLoanImage = await captureChartImage({
        chartRef: loanChartRef,
        getCanvas: () => loanChartRef.current && loanChartRef.current.canvas,
        minImageLength: 500,
      });
      const capturedCreditImage = await captureChartImage({
        chartRef: creditChartRef,
        getCanvas: () => creditChartRef.current && creditChartRef.current.canvas,
        minImageLength: 500,
      });

      setLoanChartImage(capturedLoanImage);
      setCreditChartImage(capturedCreditImage);

      // 2. Fetch Mass Data
      const [allLoans, allCredits, futureRes] = await Promise.all([
        fetchAllModuleDataForPDF("Liability"),
        fetchAllModuleDataForPDF("Credit"),
        MODULE_API.fetchFutureLoans(1).catch(() => ({ records: [] }))
      ]);

      const loansOnly = allLoans.filter(item => item.subType === "Liability");

      setPdfLoanList(loansOnly);
      setPdfCreditList(allCredits);
      setPdfFutureList(futureRes.records || []);

      // 3. Generate PDF
      setTimeout(async () => {
        const element = document.getElementById("liability-report-pdf-container");
        if (!element) {
          setDownloadLoading(false);
          if (props.onDownloadLoading) props.onDownloadLoading(false);
          return;
        }

        await exportElementToPaginatedPdf({
          element,
          fileName: `Liability_Report_${moment().format("YYYYMMDD")}.pdf`,
          margin: 15,
        });
        setDownloadLoading(false);
        if (props.onDownloadLoading) props.onDownloadLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
      if (props.onDownloadLoading) props.onDownloadLoading(false);
    }
  };

  const loanData = {
    labels: (props.allLiabilitiesForChart || []).map(item => item["Nickname of Liabilities"] || "N/A"),
    datasets: [{
      label: "Loan Balance",
      data: (props.allLiabilitiesForChart || []).map(item => item["Loan Balance"] || item["Initial Loan Amount"] || 0),
      backgroundColor: (props.allLiabilitiesForChart || []).map((_, i) => ChartColors[i % ChartColors.length]),
    }]
  };

  const creditData = {
    labels: (props.allCreditRowsForChart || []).map(item => item["Nickname of Credit"] || "N/A"),
    datasets: [
      {
        label: "Credit Balance",
        data: (props.allCreditRowsForChart || []).map(card => {
          if (card.managedBy === "Plaid") {
            return card["Credit Balance"] || card["Loan Balance"] || 0;
          } else {
            return card["Loan Balance"] || card["Credit Balance"] || 0;
          }
        }),
        backgroundColor: "#B1E3FF",
      },
      {
        label: "Credit Limit",
        data: (props.allCreditRowsForChart || []).map(card => {
          const limit = +card["Credit Limit"] || 0;
          const balance = card.managedBy === "Plaid"
            ? (card["Credit Balance"] || card["Loan Balance"] || 0)
            : (card["Loan Balance"] || card["Credit Balance"] || 0);
          const remaining = limit - balance;
          return remaining < 0 ? 0 : remaining;
        }),
        backgroundColor: "#2A3DA3",
      }
    ]
  };

  const chartOptions = {
    legend: { display: false },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      xAxes: [{ gridLines: { color: "rgba(0, 0, 0, 0)" } }],
      yAxes: [{
        ticks: {
          callback: (value) => new Intl.NumberFormat("en-US", {
            style: "currency", currency: "USD",
            minimumFractionDigits: 0, maximumFractionDigits: 0
          }).format(value)
        }
      }]
    }
  };

  const loadLiabilityReportData = async () => {
    try {
      setLoading(true);
      const [loans, credits, futureRes] = await Promise.all([
        fetchAllModuleDataForPDF("Liability"),
        fetchAllModuleDataForPDF("Credit"),
        MODULE_API.fetchFutureLoans(1).catch(() => ({ records: [] }))
      ]);

      setPdfLoanList(loans.filter(item => item.subType === "Liability"));
      setPdfCreditList(credits);
      setPdfFutureList(futureRes.records || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const name = getFAClientName();
    if (name) setClientName(name);
    if (!props.allLiabilitiesForChart && !props.allCreditRowsForChart) {
      loadLiabilityReportData();
    }
  }, [props.sharedState]);

  const displayLoans = props.allLiabilitiesForChart || pdfLoanList;
  const displayCredits = props.allCreditRowsForChart || pdfCreditList;

  const loanDataUI = {
    labels: displayLoans.map(item => item["Nickname of Liabilities"] || "N/A"),
    datasets: [{
      label: "Loan Balance",
      data: displayLoans.map(item => item["Loan Balance"] || item["Initial Loan Amount"] || 0),
      backgroundColor: displayLoans.map((_, i) => ChartColors[i % ChartColors.length]),
    }]
  };

  const creditDataUI = {
    labels: displayCredits.map(item => item["Nickname of Credit"] || "N/A"),
    datasets: [
      {
        label: "Credit Balance",
        data: displayCredits.map(card => {
          if (card.managedBy === "Plaid") {
            return card["Credit Balance"] || card["Loan Balance"] || 0;
          } else {
            return card["Loan Balance"] || card["Credit Balance"] || 0;
          }
        }),
        backgroundColor: "#B1E3FF",
      },
      {
        label: "Credit Limit",
        data: displayCredits.map(card => {
          const limit = +card["Credit Limit"] || 0;
          const balance = card.managedBy === "Plaid"
            ? (card["Credit Balance"] || card["Loan Balance"] || 0)
            : (card["Loan Balance"] || card["Credit Balance"] || 0);
          const remaining = limit - balance;
          return remaining < 0 ? 0 : remaining;
        }),
        backgroundColor: "#2A3DA3",
      }
    ]
  };

  const dataToUseUI =
    props.allLiabilitiesForChart && props.allLiabilitiesForChart.length > 0
      ? props.allLiabilitiesForChart
      : props.liabilitiesCredit || [];

  const nicknamesUI = dataToUseUI.map(
    (card) =>
      card["Nickname of Liabilities"] || card["Nickname of Credit"] || null
  );

  const barSeriesUI = [
    {
      label: "Module",
      data:
        dataToUseUI &&
        dataToUseUI.map((data) => {
          if (data.managedBy === "Plaid") {
            return (
              +data["Credit Balance"] ||
              +data["Loan Balance"] ||
              +data["Initial Loan Amount"] ||
              0
            );
          } else {
            return (
              +data["Loan Balance"] ||
              +data["Credit Balance"] ||
              +data["Initial Loan Amount"] ||
              0
            );
          }
        }),
      fill: false,
      backgroundColor:
        dataToUseUI &&
        dataToUseUI.map(
          (_, index) => ChartColors[index % ChartColors.length]
        ),
      borderColor:
        dataToUseUI &&
        dataToUseUI.map(
          (_, index) => ChartColors[index % ChartColors.length]
        ),
    },
  ];

  const chartDataUI = {
    labels: nicknamesUI,
    datasets: barSeriesUI,
  };

  const dashboardChartOptions = {
    legend: { display: false },
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      xAxes: [{ gridLines: { color: "rgba(0, 0, 0, 0)" } }],
      yAxes: [{
        ticks: {
          callback: (value) => new Intl.NumberFormat("en-US", {
            style: "currency", currency: "USD",
            minimumFractionDigits: 0, maximumFractionDigits: 0
          }).format(value)
        }
      }]
    }
  };

  return (
    <React.Fragment>
      <div className="liabiltyCard1">
        <h3 className="Reportheading">Liabilities and Credit Report</h3>
        <div className="liabilityRow">
          <div className="liliability-image-and-amount-container">
            <img src={debtImage} alt="" />
            <div className="liliability-amounts-container">
              <div style={{ display: "flex", gap: "2rem" }}>
                <div>
                  <h3>Loans</h3>
                  <h2>{renderReportValue(props.totalLoan || displayLoans.reduce((sum, item) => sum + (parseFloat(item["Loan Balance"] || item["Initial Loan Amount"]) || 0), 0))}</h2>
                </div>
                <div>
                  <h3>Credit Cards/HELOC</h3>
                  <h2>{renderReportValue(props.totalBalance || displayCredits.reduce((sum, item) => sum + (parseFloat(item["Credit Balance"]) || 0), 0))}</h2>
                </div>
              </div>
              <div>
                <h1>Total</h1>
                <h1>
                  {renderReportValue(
                    (parseFloat(props.totalLoan || displayLoans.reduce((sum, item) => sum + (parseFloat(item["Loan Balance"] || item["Initial Loan Amount"]) || 0), 0)) || 0) +
                      (parseFloat(props.totalBalance || displayCredits.reduce((sum, item) => sum + (parseFloat(item["Credit Balance"]) || 0), 0)) || 0),
                    true
                  )}
                </h1>
              </div>
            </div>
          </div>
          <div>
            <div className="reportrow">
              <div className="reportrowData">
                <span className="reportTitle"> Client Name</span>
                <span className="reportsubTitle">{clientName || localStorage.getItem("User") || ""}</span>
              </div>
              <div className="reportrowData">
                <span className="reportTitle"> Today's Date </span>
                <span className="reportsubTitle">{moment().format("MM/DD/YYYY")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DASHBOARD UI CHART - RESTORED TO ORIGINAL */}
      <div className="liabiltyCard2" style={{ position: "relative" }}>
        <h1 className="liablitycardheading">Balance</h1>
        <div style={{ marginTop: "1rem", height: "300px" }}>
          {props.loadingLiabilityChartData || loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Spin indicator={loaderIcon} />
            </div>
          ) : nicknamesUI && nicknamesUI.length > 0 ? (
            <Bar data={chartDataUI} options={dashboardChartOptions} height={300} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>No data to show</div>
          )}
        </div>
      </div>

      {/* HIDDEN CHARTS FOR PDF ONLY */}
      <div style={{ position: "fixed", top: 0, left: "-3000px", width: "1000px", zIndex: -9999, opacity: 0.01, pointerEvents: "none" }}>
        <div className="liabilityChartsContainer">
          <div className="chartHalf">
             <Bar ref={loanChartRef} data={loanDataUI} options={chartOptions} height={300} />
          </div>
          <div className="chartHalf">
             <Bar ref={creditChartRef} data={creditDataUI} options={{...chartOptions, scales: {...chartOptions.scales, xAxes: [{...chartOptions.scales.xAxes[0], stacked: true}], yAxes: [{...chartOptions.scales.yAxes[0], stacked: true}]}}} height={300} />
          </div>
        </div>

        <div id="liability-report-pdf-container">
          <LiabilityPDFReport
            loanList={pdfLoanList.length > 0 ? pdfLoanList : (props.liabilitiesCredit || [])}
            creditList={pdfCreditList.length > 0 ? pdfCreditList : (props.creditRows || [])}
            futureList={pdfFutureList.length > 0 ? pdfFutureList : (props.futureLoans || [])}
            totalLoans={props.totalLoan || displayLoans.reduce((sum, item) => sum + (parseFloat(item["Loan Balance"] || item["Initial Loan Amount"]) || 0), 0)}
            totalCredit={props.totalBalance || displayCredits.reduce((sum, item) => sum + (parseFloat(item["Credit Balance"]) || 0), 0)}
            clientName={clientName || localStorage.getItem("User") || ""}
            loanChartImage={loanChartImage}
            creditChartImage={creditChartImage}
            // Pass the summary stats
            total={props.total}
            totalNoCredit={props.totalNoCredit}
            totalMonthly={props.totalMonthly}
            totalCreditLimit={props.totalCreditLimit}
            newLiabilitySum={props.newLiabilitySum}
            liabilityCreditHigherValue={props.liabilityCreditHigherValue}
            totalMonthDiff={props.totalMonthDiff}
            loading={props.loading}
          />
        </div>
      </div>
    </React.Fragment>
  );
});
